/**
 * buildUserFlowSteps
 *
 * Builds the trailing user-content steps for the AddContent sheet —
 * Description → Content Settings → Monetization. Extracted from
 * `AddContentSheetContent` to keep the component under the size ceiling
 * (finding_4): this block is untouched by the trimmed message flows (chat /
 * community stop at the preview), so isolating it separates the stable
 * user-content config from the actively-evolving message-flow wiring.
 *
 * Behaviour-identical to the inline step objects it replaced — same ids, same
 * order, same props. Returned as `StepConfig[]` and spread after the shared
 * drop/preview steps by the caller.
 */

import type { StepConfig } from '@/presentation/components/common/stepper/ExpauseStepper';
import type { VideoMeta } from '@/domain/entities/publish/videoMeta';
import type { DescriptionResult } from '@/domain/entities/publish/descriptionResult';
import type { ContentSettingsResult } from '@/domain/entities/publish/contentSettingsResult';
import type { ContentReplyCommentInfoEntity } from '@/domain/entities/comment/contentReplyCommentInfoEntity';
import { DescriptionStep } from './steps/DescriptionStep';
import { ContentSettingsStep } from './steps/ContentSettingsStep';
import { MonetizationStep } from './steps/MonetizationStep';

interface BuildUserFlowStepsParams {
  mediaId: string;
  selectedFile: File | null;
  videoMeta: VideoMeta | null;
  descriptionResult: DescriptionResult | null;
  contentSettings: ContentSettingsResult | null;
  onDescriptionComplete: (result: DescriptionResult) => void;
  onSettingsComplete: (result: ContentSettingsResult) => void;
  onBack: () => void;
  onFinish: () => void;
  screenKey?: string;
  /**
   * Comment-reply flow only — carried to the Monetization step, the single
   * place the publish request is assembled. The Description / Settings steps
   * are untouched by it, exactly as on Flutter (the Settings step's one
   * comment-reply difference travels on `hideSchedule` instead).
   */
  contentReplyCommentInfo?: ContentReplyCommentInfoEntity | null;
  /**
   * Comment-reply flow only — the upload-result seam handed to the Monetization
   * step. See `UsePublishContentParams.onUploaded` for the contract.
   */
  onUploaded?: (success: boolean) => void;
  /**
   * Comment-reply flow only — drops the Content Settings step's Schedule row,
   * the single structural difference between the comment-reply publish chain and
   * the ordinary one (Flutter `content_settings_screen.dart:565-568`).
   */
  hideSchedule?: boolean;
}

export function buildUserFlowSteps({
  mediaId,
  selectedFile,
  videoMeta,
  descriptionResult,
  contentSettings,
  onDescriptionComplete,
  onSettingsComplete,
  onBack,
  onFinish,
  screenKey,
  contentReplyCommentInfo,
  onUploaded,
  hideSchedule,
}: BuildUserFlowStepsParams): StepConfig[] {
  return [
    {
      id: 'description',
      content: (
        <DescriptionStep
          onNext={onDescriptionComplete}
          onBack={onBack}
          screenKey={screenKey}
        />
      ),
    },
    {
      id: 'content-settings',
      content: (
        <ContentSettingsStep
          mediaId={mediaId}
          onNext={onSettingsComplete}
          onBack={onBack}
          screenKey={screenKey}
          hideSchedule={hideSchedule}
        />
      ),
    },
    {
      id: 'monetization',
      content: (
        <MonetizationStep
          mediaId={mediaId}
          selectedFile={selectedFile}
          videoMeta={videoMeta}
          descriptionResult={descriptionResult}
          contentSettings={contentSettings}
          onBack={onBack}
          onFinish={onFinish}
          screenKey={screenKey}
          contentReplyCommentInfo={contentReplyCommentInfo}
          onUploaded={onUploaded}
        />
      ),
    },
  ];
}
